import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, Calendar as CalIcon, Link2, Clock,
  Trash2, ExternalLink, AlertCircle, CheckCircle2, X, ListChecks,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Event type config ─────────────────────────────────────────────
const EVENT_TYPES = {
  site_visit:      { label: 'Site Visit',     color: '#2D6B68', bg: '#E5F0EE' },
  client_meeting:  { label: 'Client Meeting', color: '#5B6CFF', bg: '#EEF0FF' },
  delivery:        { label: 'Delivery',       color: '#C7892A', bg: '#FFF6E5' },
  install:         { label: 'Install',        color: '#4C8B4F', bg: '#EDF6EE' },
  time_block:      { label: 'Time Block',     color: '#8B4C8F', bg: '#F5EDF6' },
  other:           { label: 'Other',          color: '#6B7A73', bg: '#F0F3F1' },
};

// ─── Calendar grid helpers ─────────────────────────────────────────
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function fmtYMD(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Build a 6-row × 7-col grid for the month view (leading + trailing days).
function buildMonthGrid(viewDate) {
  const first = startOfMonth(viewDate);
  const last = endOfMonth(viewDate);
  const startOffset = first.getDay(); // 0 = Sun
  const days = [];
  // leading
  for (let i = startOffset; i > 0; i--) {
    days.push(new Date(first.getFullYear(), first.getMonth(), 1 - i));
  }
  // current month
  for (let d = 1; d <= last.getDate(); d++) {
    days.push(new Date(first.getFullYear(), first.getMonth(), d));
  }
  // trailing to fill 42 cells
  while (days.length < 42) {
    const prev = days[days.length - 1];
    days.push(new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 1));
  }
  return days;
}

// Group events by YYYY-MM-DD
function groupEventsByDay(events) {
  const map = {};
  events.forEach(ev => {
    const s = ev.start;
    if (!s) return;
    const key = s.slice(0, 10);
    (map[key] ||= []).push(ev);
  });
  return map;
}

// ─── Main ──────────────────────────────────────────────────────────
export default function SchedulePage() {
  const [viewDate, setViewDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(null);   // null | true | false
  const [hasCalendar, setHasCalendar] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  // ── Connection / bootstrap ───────────────────────────────────
  const checkStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/calendar/status`, { credentials: 'include' });
      if (res.ok) {
        const s = await res.json();
        setConnected(!!s.connected);
        setHasCalendar(!!s.has_calendar);
        return s;
      }
    } catch { /* ignore */ }
    setConnected(false);
    return null;
  }, []);

  useEffect(() => {
    checkStatus();
    fetch(`${API}/estimates`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setEstimates)
      .catch(() => {});
  }, [checkStatus]);

  // ── Fetch events for the visible month ──────────────────────
  const fetchEvents = useCallback(async () => {
    if (!hasCalendar) return;
    setLoading(true);
    try {
      const first = startOfMonth(viewDate);
      const last = endOfMonth(viewDate);
      // pad ±1 week so leading/trailing weeks show their events
      const rangeStart = new Date(first); rangeStart.setDate(rangeStart.getDate() - 7);
      const rangeEnd = new Date(last); rangeEnd.setDate(rangeEnd.getDate() + 7);
      const res = await fetch(
        `${API}/calendar/events?start=${fmtYMD(rangeStart)}&end=${fmtYMD(rangeEnd)}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        setEvents(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        if (err.detail?.code === 'needs_calendar_scope') {
          setHasCalendar(false);
        } else {
          toast.error(err.detail?.message || err.detail || 'Failed to load events');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [viewDate, hasCalendar]);

  useEffect(() => { if (hasCalendar) fetchEvents(); }, [fetchEvents, hasCalendar]);

  // ── Connect flow — reuses existing Gmail OAuth ───────────────
  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await fetch(`${API}/gmail/auth-url`, { credentials: 'include' });
      if (!res.ok) {
        toast.error('Failed to start Google connect flow');
        return;
      }
      const { auth_url } = await res.json();
      // Remember where to land after Google bounces us back
      sessionStorage.setItem('gmail_return_to', '/schedule');
      window.location.href = auth_url;
    } finally {
      setConnecting(false);
    }
  };

  // ── Calendar grid ────────────────────────────────────────────
  const monthGrid = useMemo(() => buildMonthGrid(viewDate), [viewDate]);
  const eventsByDay = useMemo(() => groupEventsByDay(events), [events]);
  const estimateById = useMemo(() => {
    const m = new Map();
    estimates.forEach(e => m.set(e.estimate_id, e));
    return m;
  }, [estimates]);

  // ── Create/edit/delete handlers ──────────────────────────────
  const openNewEvent = (day = null) => {
    setSelectedDay(day);
    setEditingEvent(null);
    setEditorOpen(true);
  };

  const openExistingEvent = (event) => {
    setEditingEvent(event);
    setSelectedDay(null);
    setEditorOpen(true);
  };

  const handleSaveEvent = async (payload, eventId) => {
    const url = eventId ? `${API}/calendar/events/${eventId}` : `${API}/calendar/events`;
    const method = eventId ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method, headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify(payload),
    });
    if (res.ok) {
      toast.success(eventId ? 'Event updated' : 'Event created');
      setEditorOpen(false);
      fetchEvents();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Failed to save event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event? This also removes it from your Google Calendar.')) return;
    const res = await fetch(`${API}/calendar/events/${eventId}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) {
      toast.success('Event deleted');
      setEditorOpen(false);
      fetchEvents();
    } else {
      toast.error('Failed to delete');
    }
  };

  // ── Renders ──────────────────────────────────────────────────
  if (connected === null) {
    return (
      <div className="animate-fade-in flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!connected || !hasCalendar) {
    return (
      <div className="animate-fade-in max-w-xl" data-testid="calendar-connect-panel">
        <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1D1C] mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>
          Schedule
        </h1>
        <p className="text-sm text-[#6B7A73] mb-6">
          Plan site visits, client meetings, deliveries and installs. Link events to estimates and optionally log hours against the job's costs.
        </p>
        <div className="bg-white border border-[#E6EBE8] rounded-md p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-700" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-[#1A1D1C] mb-1">
                {!connected ? 'Connect your Google account' : 'Calendar access required'}
              </h3>
              <p className="text-sm text-[#6B7A73] mb-4">
                {!connected
                  ? 'Schedule syncs with Google Calendar so events stay in one place — your phone, laptop, and this app. Uses the same Google account you already use for Gmail.'
                  : 'You connected Gmail previously but didn\'t grant calendar access yet. Reconnect Google to enable calendar sync — your Gmail access stays intact.'}
              </p>
              <Button
                onClick={handleConnect}
                disabled={connecting}
                className="bg-[#2D6B68] text-white hover:bg-[#235552]"
                data-testid="connect-google-btn"
              >
                <CalIcon className="w-4 h-4 mr-2" />
                {connecting ? 'Redirecting…' : (connected ? 'Reconnect Google (add Calendar)' : 'Connect Google Calendar')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const monthLabel = viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const today = new Date();

  return (
    <div className="animate-fade-in" data-testid="schedule-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Schedule
          </h1>
          <p className="text-xs text-[#6B7A73] mt-1 flex items-center gap-1.5">
            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
            Synced with Google Calendar · {events.length} event{events.length !== 1 ? 's' : ''} this view
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setViewDate(new Date())}
            variant="outline"
            className="border-[#E6EBE8] text-[#3A423E] hover:bg-[#F5F6F5]"
            data-testid="today-btn"
          >
            Today
          </Button>
          <div className="flex items-center gap-1">
            <Button
              onClick={() => setViewDate(d => addMonths(d, -1))}
              variant="ghost" size="icon"
              className="hover:bg-[#F0F3F1]"
              data-testid="prev-month-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-semibold text-[#1A1D1C] min-w-[140px] text-center" data-testid="month-label">
              {monthLabel}
            </span>
            <Button
              onClick={() => setViewDate(d => addMonths(d, 1))}
              variant="ghost" size="icon"
              className="hover:bg-[#F0F3F1]"
              data-testid="next-month-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Button
            onClick={() => setBulkOpen(true)}
            variant="outline"
            className="border-[#E6EBE8] text-[#3A423E] hover:bg-[#F5F6F5]"
            data-testid="from-tasks-btn"
          >
            <ListChecks className="w-4 h-4 mr-2" /> From Tasks
          </Button>
          <Button
            onClick={() => openNewEvent()}
            className="bg-[#2D6B68] text-white hover:bg-[#235552]"
            data-testid="new-event-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Event
          </Button>
        </div>
      </div>

      {/* Month grid */}
      <div className="bg-white border border-[#E6EBE8] rounded-md overflow-hidden" data-testid="calendar-grid">
        <div className="grid grid-cols-7 border-b border-[#E6EBE8] bg-[#F9FAFA]">
          {WEEKDAYS.map(d => (
            <div key={d} className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#6B7A73] font-semibold text-center">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 grid-rows-6" style={{ minHeight: '560px' }}>
          {monthGrid.map((day, idx) => {
            const key = fmtYMD(day);
            const dayEvents = eventsByDay[key] || [];
            const inMonth = day.getMonth() === viewDate.getMonth();
            const isToday = isSameDay(day, today);
            return (
              <div
                key={idx}
                className={`border-r border-b border-[#E6EBE8] p-1.5 flex flex-col cursor-pointer hover:bg-[#F9FAFA] transition-colors ${
                  !inMonth ? 'bg-[#FAFBFB]' : 'bg-white'
                } ${(idx + 1) % 7 === 0 ? 'border-r-0' : ''}`}
                onClick={() => openNewEvent(day)}
                data-testid={`day-cell-${key}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-medium ${
                    isToday
                      ? 'bg-[#2D6B68] text-white w-5 h-5 rounded-full flex items-center justify-center'
                      : inMonth ? 'text-[#1A1D1C]' : 'text-[#C8D4CE]'
                  }`} style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    {day.getDate()}
                  </span>
                  {dayEvents.length > 3 && (
                    <span className="text-[9px] text-[#6B7A73]">+{dayEvents.length - 3}</span>
                  )}
                </div>
                <div className="space-y-0.5 overflow-hidden flex-1">
                  {dayEvents.slice(0, 3).map(ev => {
                    const cfg = EVENT_TYPES[ev.event_type] || EVENT_TYPES.other;
                    return (
                      <button
                        key={ev.event_id}
                        onClick={(e) => { e.stopPropagation(); openExistingEvent(ev); }}
                        className="w-full text-left text-[10px] px-1.5 py-0.5 rounded truncate hover:opacity-80 transition-opacity"
                        style={{ backgroundColor: cfg.bg, color: cfg.color }}
                        data-testid={`event-${ev.event_id}`}
                        title={ev.title}
                      >
                        {!ev.all_day && ev.start && (
                          <span className="font-mono mr-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                            {ev.start.slice(11, 16)}
                          </span>
                        )}
                        {ev.title}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {loading && (
        <div className="mt-3 text-center text-xs text-[#6B7A73]">Loading events…</div>
      )}

      {/* Event editor */}
      <EventEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        event={editingEvent}
        defaultDay={selectedDay}
        estimates={estimates}
        estimateById={estimateById}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
      />

      {/* Bulk from tasks */}
      <BulkFromTasksDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        estimateById={estimateById}
        onComplete={fetchEvents}
      />
    </div>
  );
}

// ─── Event Editor Dialog ───────────────────────────────────────────

function toDateInput(iso) {
  if (!iso) return '';
  return iso.slice(0, 10);
}
function toTimeInput(iso) {
  if (!iso || iso.length <= 10) return '';
  return iso.slice(11, 16);
}
function combineToIso(dateStr, timeStr) {
  if (!dateStr) return '';
  if (!timeStr) return dateStr;
  // Use local ISO with the browser TZ offset so Google stores it correctly
  const d = new Date(`${dateStr}T${timeStr}:00`);
  if (Number.isNaN(d.getTime())) return '';
  const tzOffMin = -d.getTimezoneOffset();
  const sign = tzOffMin >= 0 ? '+' : '-';
  const abs = Math.abs(tzOffMin);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${dateStr}T${timeStr}:00${sign}${hh}:${mm}`;
}

function EventEditorDialog({ open, onOpenChange, event, defaultDay, estimates, estimateById, onSave, onDelete }) {
  const isEdit = !!event;
  const [form, setForm] = useState(() => defaultForm(event, defaultDay));

  useEffect(() => {
    if (open) setForm(defaultForm(event, defaultDay));
  }, [open, event, defaultDay]);

  const update = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error('Give the event a title');
      return;
    }
    const payload = {
      title: form.title.trim(),
      description: form.description || '',
      location: form.location || '',
      all_day: !!form.all_day,
      event_type: form.event_type || 'other',
      estimate_id: form.estimate_id || null,
      hours_logged: form.hours_logged ? Number(form.hours_logged) : null,
    };
    if (form.all_day) {
      payload.start = form.startDate;
      // Google's all-day end is exclusive — add a day
      const nextDay = new Date(`${form.endDate || form.startDate}T00:00:00`);
      nextDay.setDate(nextDay.getDate() + 1);
      payload.end = fmtYMD(nextDay);
    } else {
      payload.start = combineToIso(form.startDate, form.startTime);
      payload.end = combineToIso(form.endDate || form.startDate, form.endTime);
      if (!payload.start || !payload.end) {
        toast.error('Pick valid start and end times');
        return;
      }
    }
    await onSave(payload, event?.event_id);
  };

  const estObj = form.estimate_id ? estimateById.get(form.estimate_id) : null;
  const canLogHours = !!form.estimate_id && !form.all_day;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-md border border-[#E6EBE8] max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>
            {isEdit ? 'Edit event' : 'New event'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Title</label>
            <Input
              autoFocus
              value={form.title}
              onChange={e => update({ title: e.target.value })}
              className="mt-1 bg-white border-[#E6EBE8]"
              placeholder="Site visit — Smith residence"
              data-testid="event-title-input"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Event type</label>
            <Select value={form.event_type} onValueChange={v => update({ event_type: v })}>
              <SelectTrigger className="mt-1 bg-white border-[#E6EBE8]" data-testid="event-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EVENT_TYPES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <label className="flex items-center gap-2 text-sm text-[#3A423E] cursor-pointer">
            <Checkbox
              checked={form.all_day}
              onCheckedChange={v => update({ all_day: !!v })}
              data-testid="all-day-checkbox"
            />
            All-day event
          </label>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">
                {form.all_day ? 'Start date' : 'Start'}
              </label>
              <div className="flex gap-1 mt-1">
                <Input
                  type="date"
                  value={form.startDate}
                  onChange={e => update({ startDate: e.target.value })}
                  className="bg-white border-[#E6EBE8] text-sm"
                  data-testid="event-start-date"
                />
                {!form.all_day && (
                  <Input
                    type="time"
                    value={form.startTime}
                    onChange={e => update({ startTime: e.target.value })}
                    className="bg-white border-[#E6EBE8] text-sm w-24"
                    data-testid="event-start-time"
                  />
                )}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">
                {form.all_day ? 'End date' : 'End'}
              </label>
              <div className="flex gap-1 mt-1">
                <Input
                  type="date"
                  value={form.endDate}
                  onChange={e => update({ endDate: e.target.value })}
                  className="bg-white border-[#E6EBE8] text-sm"
                  data-testid="event-end-date"
                />
                {!form.all_day && (
                  <Input
                    type="time"
                    value={form.endTime}
                    onChange={e => update({ endTime: e.target.value })}
                    className="bg-white border-[#E6EBE8] text-sm w-24"
                    data-testid="event-end-time"
                  />
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Location (optional)</label>
            <Input
              value={form.location}
              onChange={e => update({ location: e.target.value })}
              className="mt-1 bg-white border-[#E6EBE8]"
              placeholder="123 Main St, Montreal"
              data-testid="event-location-input"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Link to estimate (optional)</label>
            <Select
              value={form.estimate_id || '__none__'}
              onValueChange={v => update({ estimate_id: v === '__none__' ? null : v })}
            >
              <SelectTrigger className="mt-1 bg-white border-[#E6EBE8]" data-testid="event-estimate-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No estimate</SelectItem>
                {estimates.map(e => (
                  <SelectItem key={e.estimate_id} value={e.estimate_id}>
                    {e.estimate_number} — {(e.title || '').slice(0, 30)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {estObj && (
              <p className="text-[11px] text-[#6B7A73] mt-1 flex items-center gap-1">
                <Link2 className="w-3 h-3" /> {estObj.client?.company || estObj.client?.name || estObj.title}
              </p>
            )}
          </div>

          {/* Hours logging — only makes sense for timed events on an estimate */}
          {canLogHours && (
            <div className="bg-[#F9FAFA] border border-[#E6EBE8] rounded-md p-3">
              <label className="text-[10px] uppercase tracking-wider text-[#6B7A73] flex items-center gap-1">
                <Clock className="w-3 h-3" /> Log hours on this estimate's Job Costs
              </label>
              <p className="text-[11px] text-[#6B7A73] mt-1 mb-2 leading-relaxed">
                Enter the billable hours this event represents. A time entry is pushed to the linked estimate's Job Costs when you save.
              </p>
              <Input
                type="number" step="0.25" min="0"
                placeholder="e.g., 2.5"
                value={form.hours_logged}
                onChange={e => update({ hours_logged: e.target.value })}
                className="bg-white border-[#E6EBE8] text-sm max-w-[120px]"
                data-testid="event-hours-input"
              />
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Description (optional)</label>
            <Textarea
              value={form.description}
              onChange={e => update({ description: e.target.value })}
              rows={2}
              className="mt-1 resize-none bg-white border-[#E6EBE8] text-sm"
              placeholder="Details, attendees, notes…"
              data-testid="event-description-input"
            />
          </div>
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          {event?.html_link && (
            <Button
              variant="ghost"
              onClick={() => window.open(event.html_link, '_blank')}
              className="text-[#6B7A73] hover:bg-[#F0F3F1] mr-auto"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open in Google
            </Button>
          )}
          {isEdit && (
            <Button
              variant="outline"
              onClick={() => onDelete(event.event_id)}
              className="border-red-200 text-red-600 hover:bg-red-50"
              data-testid="delete-event-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E6EBE8]">
            <X className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
          <Button onClick={submit} className="bg-[#2D6B68] text-white hover:bg-[#235552]" data-testid="save-event-btn">
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function defaultForm(event, defaultDay) {
  if (event) {
    return {
      title: event.title || '',
      description: event.description || '',
      location: event.location || '',
      event_type: event.event_type || 'other',
      all_day: !!event.all_day,
      startDate: toDateInput(event.start),
      startTime: event.all_day ? '' : toTimeInput(event.start),
      endDate: toDateInput(event.end),
      endTime: event.all_day ? '' : toTimeInput(event.end),
      estimate_id: event.estimate_id || null,
      hours_logged: event.hours_logged || '',
    };
  }
  const base = defaultDay || new Date();
  const startDate = fmtYMD(base);
  const startTime = `${String(new Date().getHours()).padStart(2, '0')}:00`;
  const endHour = Math.min(new Date().getHours() + 1, 23);
  return {
    title: '', description: '', location: '',
    event_type: 'site_visit',
    all_day: false,
    startDate, startTime,
    endDate: startDate,
    endTime: `${String(endHour).padStart(2, '0')}:00`,
    estimate_id: null, hours_logged: '',
  };
}

// ─── Bulk-from-tasks Dialog ────────────────────────────────────────

function BulkFromTasksDialog({ open, onOpenChange, estimateById, onComplete }) {
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${API}/calendar/sync-candidates`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setCandidates(data);
        setSelected(new Set(data.map(t => t.task_id)));  // default: all selected
      })
      .catch(() => toast.error('Failed to load sync candidates'))
      .finally(() => setLoading(false));
  }, [open]);

  const toggleAll = () => {
    setSelected(prev => prev.size === candidates.length ? new Set() : new Set(candidates.map(t => t.task_id)));
  };
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (selected.size === 0) {
      toast.error('Pick at least one task');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/calendar/from-tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ task_ids: Array.from(selected) }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail?.message || err.detail || 'Bulk import failed');
        return;
      }
      const out = await res.json();
      const created = out.created?.length || 0;
      const skipped = out.skipped?.length || 0;
      const failed = out.failed?.length || 0;
      if (created > 0) {
        toast.success(
          `Created ${created} event${created !== 1 ? 's' : ''}` +
          (skipped ? ` · skipped ${skipped}` : '') +
          (failed ? ` · ${failed} failed` : '')
        );
      } else {
        toast.warning('Nothing created — all tasks were already synced or invalid.');
      }
      onComplete?.();
      onOpenChange(false);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-md border border-[#E6EBE8] max-w-xl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>
            Bulk-create events from Tasks
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#6B7A73]">
          Tasks with a due date that aren't already on your calendar. Each selected task becomes an all-day event and is linked back to its task (and estimate, if set).
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-5 h-5 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : candidates.length === 0 ? (
          <div className="text-center py-8 text-sm text-[#6B7A73]">
            No eligible tasks. Add a due date to a task (or un-sync an event by deleting it) to see it here.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs text-[#6B7A73] pb-1 border-b border-[#E6EBE8]">
              <button
                onClick={toggleAll}
                className="font-semibold uppercase tracking-wider hover:text-[#2D6B68]"
                data-testid="toggle-all-btn"
              >
                {selected.size === candidates.length ? 'Deselect all' : 'Select all'}
              </button>
              <span>{selected.size} / {candidates.length} selected</span>
            </div>
            <div className="max-h-72 overflow-y-auto -mx-1 pr-1">
              {candidates.map(t => {
                const est = t.estimate_id ? estimateById.get(t.estimate_id) : null;
                const checked = selected.has(t.task_id);
                return (
                  <label
                    key={t.task_id}
                    className={`flex items-start gap-2 px-2 py-2 rounded cursor-pointer hover:bg-[#F9FAFA] ${
                      checked ? 'bg-[#F5FAF9]' : ''
                    }`}
                    data-testid={`candidate-${t.task_id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleOne(t.task_id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1A1D1C] truncate">{t.title}</p>
                      <div className="flex items-center flex-wrap gap-2 mt-0.5 text-[11px] text-[#6B7A73]">
                        <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                          {new Date(t.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </span>
                        {t.priority === 'high' && <span className="text-red-600 font-semibold">High</span>}
                        {est && (
                          <span className="inline-flex items-center gap-1 text-[#C7892A]">
                            <Link2 className="w-2.5 h-2.5" />
                            {est.estimate_number}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E6EBE8]">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={creating || selected.size === 0 || candidates.length === 0}
            className="bg-[#2D6B68] text-white hover:bg-[#235552]"
            data-testid="bulk-create-btn"
          >
            {creating ? 'Creating…' : `Create ${selected.size} event${selected.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

